import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {CounsellingWorkUpdateService} from "../counselling-work-update.service";
import {AddEditWorkUpdateComponent} from "../add-edit-work-update/add-edit-work-update.component";
import {MatDialog} from "@angular/material/dialog";
import {
  ViewCounsellingSessionReviewComponent
} from "../../CounsellingSessionReview/view-counselling-session-review/view-counselling-session-review.component";
import {ViewWorkUpdateComponent} from "../view-work-update/view-work-update.component";
import {Observable, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {EmployeeService} from "../../../../../services/employee.service";
import {LogService} from "../../../../../services/log.service";
import {FinancialYearService} from "../../../../../services/financial-year.service";
@Component({
  selector: 'app-work-update-list',
  templateUrl: './work-update-list.component.html',
  styleUrls: ['./work-update-list.component.scss']
})
export class WorkUpdateListComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  displayedColumns = ['centre','date','submittedBy', 'task', 'userSubmittedBy', 'actions'];
  dataSource = new MatTableDataSource<any>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  start: Date | undefined
  end: Date | undefined
  counsellors$: Observable<any[]>
  submittedBy: string[] = []
  // holds selected counsellor objects coming from searchable-select
  selectedCounsellors: any[] = [];

  // Store the original unfiltered data
  private allWorkUpdates: any[] = [];
  userData = JSON.parse(localStorage.getItem('userData')!).email

  constructor(private service: CounsellingWorkUpdateService,
              private router: Router,
              private snackBar: MatSnackBar,
              private dialog: MatDialog,
              private readonly mEmployeeService: EmployeeService,
              private readonly financialYearService: FinancialYearService,
              private mLogService: LogService,) {
    this.counsellors$ = this.mEmployeeService.getCounsellers()
  }

  ngOnInit(): void {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.start = fy.startDate;
    this.end = fy.endDate;
    this.loadWorkUpdates();
  }

  /**
   * Handler for searchable-select selectionChange. The component emits the
   * selected items (objects). We convert to the existing `submittedBy` string[]
   * which stores counsellor userName values used by the filtering logic.
   */
  onCounsellorsSelected(selected: any[]) {
    this.selectedCounsellors = selected || [];
    // Map selection to array of userName strings to keep existing filter logic
    this.submittedBy = (this.selectedCounsellors || [])
      .map(s => (typeof s === 'string' ? s : s?.userName))
      .filter(Boolean);
  }

  loadWorkUpdates() {
    this.service.getAllWorkUpdates().pipe(takeUntil(this._destroyed$)).subscribe(data => {
      console.log(data);
      this.allWorkUpdates = data;
      this.dataSource.data = data;
      this.dataSource.paginator = this.paginator;
    });
  }

  // Search functionality
  onSearch() {
    let filteredData = [...this.allWorkUpdates];

    // Filter by date range
    if (this.start && this.end) {
      filteredData = filteredData.filter(item => {
        const itemDate = item.date.toDate();
        const startDate = new Date(this.start!);
        const endDate = new Date(this.end!);

        // Set time to start of day for start date and end of day for end date
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // Filter by submitted by (counsellor) - handle multiple selections
    if (this.submittedBy && this.submittedBy.length > 0) {
      filteredData = filteredData.filter(item =>
        this.submittedBy.includes(item.counsellorName)
      );
    }

    // Update the table
    this.dataSource.data = filteredData;

    // Reset paginator to first page
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  // Clear filters
  onClear() {
    const fy = this.financialYearService.getCurrentFinancialYear();
    this.start = fy.startDate;
    this.end = fy.endDate;
    this.submittedBy = [];

    // Show all data (picker reset to FY, but not applied until Search is clicked)
    this.dataSource.data = this.allWorkUpdates;

    // Reset paginator to first page
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  delete(id: string) {
    const confirmed = window.confirm('Are you sure you want to delete this work update?');
    if (confirmed) {
      this.service.deleteWorkUpdate(id)
        .then(() => {
          this.snackBar.open('Work update deleted successfully.', 'Close', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['snackbar-success']
          });
          const activity = {
            date: new Date().getTime(),
            section: 'Work Updates',
            action: 'Deleted',
            description: `Work Update details deleted by user`,
            currentIp: localStorage.getItem('currentip')!
          };
          this.mLogService.addLog(activity);
        })
        .catch(err => {
          console.error(err);
          this.snackBar.open('Failed to delete work update.', 'Close', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
          });
        });
    }
  }

  Add() {
    const dialogRef = this.dialog.open(AddEditWorkUpdateComponent, {
      width: '800px',
      maxHeight: '90vh',
      disableClose: true,
      data: { id: null }
    });

    dialogRef.afterClosed().subscribe((result:any) => {
      if (result === 'saved') {
        this.refreshTable();
      }
    });
  }

  viewEdit(id: string) {
    const dialogRef = this.dialog.open(AddEditWorkUpdateComponent, {
      width: '800px',
      maxHeight: '90vh',
      disableClose: true,
      data: { id }
    });

    dialogRef.afterClosed().subscribe((result:any) => {
      if (result === 'updated') {
        this.refreshTable();
      }
    });
  }

  viewServicingDetails(element:any){
    this.dialog.open(ViewWorkUpdateComponent, {
      data:element,
      width: '700px'
    })
  }

  refreshTable() {
    this.service.getAllWorkUpdates().pipe(takeUntil(this._destroyed$)).subscribe(data => {
      this.allWorkUpdates = data;
      this.dataSource.data = data;
      // Reset filters back to the current financial year (picker only, not applied)
      const fy = this.financialYearService.getCurrentFinancialYear();
      this.start = fy.startDate;
      this.end = fy.endDate;
      this.submittedBy = [];
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  canShowDelete(element: any): boolean {
    if (this.userData === 'ldcounsellor@accesslife.org' || this.userData === 'test1@gmail.com') {
      return true;
    }

    if (!element?.date) {
      return false;
    }

    const createdDate = element.date.toDate();
    const now = new Date();

    const diffInMs = now.getTime() - createdDate.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays <= 3;
  }


  getAllCentreNames(row: any): string {
    if (!row?.centre || row.centre.length === 0) return '';

    return row.centre.map((c: any) => c.centreName).join(', ');
  }


  protected readonly PermissionType = PermissionType;

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
